import { useEffect, useRef } from 'react';
import Button from '@/components/common/Button';
import type { IssueTask } from '@/utils/receiptIssuance';

interface Props {
  tasks: IssueTask[];
  running: boolean;
  completed: boolean;
  saved: boolean;
  canReturnToForm: boolean;
  onRetry: () => void;
  onContinue: () => void;
  onReturn: () => void;
}

export default function ReceiptIssueDialog({ tasks, running, completed, saved, canReturnToForm, onRetry, onContinue, onReturn }: Props) {
  const dialog = useRef<HTMLDialogElement>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = dialog.current!;
    element.showModal();
    element.focus();
    return () => element.close();
  }, []);
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [tasks]);

  return (
    <dialog ref={dialog} aria-labelledby="receipt-issue-title" aria-describedby="receipt-issue-description"
      tabIndex={-1} onCancel={event => event.preventDefault()}
      className="receipt-issue-dialog w-[calc(100%-2rem)] max-w-xl rounded-2xl bg-white p-0 shadow-2xl">
      <div className="border-b border-gray-100 px-6 py-5">
        <h2 id="receipt-issue-title" className="text-xl font-semibold text-gray-900">Emisión de recibos</h2>
        <p id="receipt-issue-description" className="mt-1 text-sm text-gray-600">
          {completed ? 'Todos los pasos finalizaron correctamente.' : running ? 'Espere mientras completamos cada paso.' : 'La emisión requiere atención.'}
        </p>
      </div>
      <div className="max-h-[55vh] overflow-y-auto px-6 py-5" aria-live="polite" aria-relevant="additions text">
        <ol className="space-y-5">
          {tasks.map(task => (
            <li key={task.id} className="receipt-issue-task flex gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center" aria-hidden="true">
                {task.status === 'running' ? <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-primary motion-reduce:animate-none" />
                  : task.status === 'success' ? <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 font-bold text-green-700">✓</span>
                    : <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 font-bold text-red-700">!</span>}
              </span>
              <div className="min-w-0">
                <p className={`text-sm font-medium ${task.status === 'error' ? 'text-red-700' : 'text-gray-900'}`}>
                  <span className="sr-only">{task.status === 'success' ? 'Completado: ' : task.status === 'error' ? 'Error: ' : 'En curso: '}</span>
                  {task.label}
                </p>
                {task.detail && <p className={`mt-1 break-words text-xs ${task.status === 'error' ? 'text-red-700' : 'text-gray-600'}`}>{task.detail}</p>}
              </div>
            </li>
          ))}
        </ol>
        <div ref={end} />
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
        {!running && !completed && saved && <>
          <p className="w-full text-sm text-amber-800">El recibo está guardado. Reintentar retoma los pasos pendientes sin duplicarlo.</p>
          <Button onClick={onRetry}>Reintentar pasos pendientes</Button>
        </>}
        {!running && !completed && !saved && <Button variant="secondary" onClick={onReturn}>
          {canReturnToForm ? 'Volver al formulario' : 'Revisar recibos'}
        </Button>}
        {!running && !completed && !saved && !canReturnToForm && <p className="w-full text-sm text-amber-800">
          No se pudo confirmar si el recibo se guardó. Revise el listado antes de volver a crearlo.
        </p>}
        {completed && <Button onClick={onContinue}>Continuar</Button>}
        {running && <p className="text-xs text-gray-500">No cierre esta ventana hasta finalizar.</p>}
      </div>
    </dialog>
  );
}
